import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { formatDate } from '../utilities/date';
import {
  AssessmentFilters,
  IAssessment,
  IDetailedAssessment,
  initAssessmentFilters
} from 'types/assessments';
import { IArea } from 'types/area';
import { IBlock } from 'types/methologies';
import { IAudit, IDetailedAudit } from 'types/audits';
import { useAssessmentsQuery } from 'hooks/queries/useAssessmentsQuery';
import { useAuditsQuery } from 'hooks/queries/useAuditsQuery';

export interface IAssessmentContext {
  tabIndex: number;
  setTabIndex: React.Dispatch<React.SetStateAction<number>>;
  areas: IArea[];
  setAreas: React.Dispatch<React.SetStateAction<IArea[]>>;
  assessments: IAssessment[];
  setAssessments: React.Dispatch<React.SetStateAction<IAssessment[]>>;
  selectedBlock: IBlock | undefined;
  setSelectedBlock: React.Dispatch<React.SetStateAction<IBlock | undefined>>;
  selectedAssessments: IDetailedAssessment[];
  setSelectedAssessments: React.Dispatch<
    React.SetStateAction<IDetailedAssessment[]>
  >;
  selectedAssessment: IDetailedAssessment | undefined;
  setSelectedAssessment: React.Dispatch<
    React.SetStateAction<IDetailedAssessment | undefined>
  >;
  assessmentToEdit: IDetailedAssessment | undefined;
  setAssessmentToEdit: React.Dispatch<
    React.SetStateAction<IDetailedAssessment | undefined>
  >;
  assessmentToEditId: IDetailedAssessment['id'] | undefined;
  setAssessmentToEditId: React.Dispatch<
    React.SetStateAction<IDetailedAssessment['id'] | undefined>
  >;
  assessmentsToLaunch: IDetailedAssessment[];
  setAssessmentsToLaunch: React.Dispatch<
    React.SetStateAction<IDetailedAssessment[]>
  >;
  filters: AssessmentFilters;
  defaultFilters: Partial<AssessmentFilters>;
  savedFilters: Partial<AssessmentFilters>;
  setFilters: React.Dispatch<React.SetStateAction<AssessmentFilters>>;
  setDefaultFilters: React.Dispatch<
    React.SetStateAction<Partial<AssessmentFilters>>
  >;
  setSavedFilters: React.Dispatch<
    React.SetStateAction<Partial<AssessmentFilters>>
  >;
  getDateLabel: (
    filterName: string,
    from: Date | null,
    to: Date | null
  ) => string;
  audits: IAudit[];
  setAudits: React.Dispatch<React.SetStateAction<IAudit[]>>;
  selectedAudit: IDetailedAudit | undefined;
  setSelectedAudit: React.Dispatch<
    React.SetStateAction<IDetailedAudit | undefined>
  >;
  selectedAudits: IDetailedAudit[];
  setSelectedAudits: React.Dispatch<React.SetStateAction<IDetailedAudit[]>>;
  selectedAuditId?: IAudit['id'];
  setSelectedAuditId: React.Dispatch<
    React.SetStateAction<IAudit['id'] | undefined>
  >;
  isAuditCriteriaModalOpen: boolean;
  setIsAuditCriteriaModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isEditAuditModalOpen: boolean;
  setIsEditAuditModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isCreateAuditModalOpen: boolean;
  setIsCreateAuditModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleLoadAssessments: () => void;
  handleLoadAudits: () => void;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  auditsPage: number;
  setAuditsPage: React.Dispatch<React.SetStateAction<number>>;
  auditsPerPage: number;
  setAuditsPerPage: React.Dispatch<React.SetStateAction<number>>;
  auditsPaginationTotal: number;
  setAuditsPaginationTotal: React.Dispatch<React.SetStateAction<number>>;
}

const AssessmentContext = React.createContext<IAssessmentContext | undefined>(
  undefined
);

export const AssessmentContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [areas, setAreas] = useState<IArea[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<IBlock>();
  const [selectedAssessment, setSelectedAssessment] = useState<
    IDetailedAssessment | undefined
  >();
  const [selectedAssessments, setSelectedAssessments] = useState<
    IDetailedAssessment[]
  >([]);
  const [assessmentToEdit, setAssessmentToEdit] =
    useState<IDetailedAssessment>();
  const [assessmentToEditId, setAssessmentToEditId] =
    useState<IDetailedAssessment['id']>();
  const [assessmentsToLaunch, setAssessmentsToLaunch] = useState<
    IDetailedAssessment[]
  >([]);
  const [defaultFilters, setDefaultFilters] = useState<
    Partial<AssessmentFilters>
  >({});
  const [savedFilters, setSavedFilters] = useState<Partial<AssessmentFilters>>(
    {}
  );
  const [filters, setFilters] = useState<AssessmentFilters>(
    initAssessmentFilters
  );
  const [selectedAudit, setSelectedAudit] = useState<
    IDetailedAudit | undefined
  >();
  const [selectedAudits, setSelectedAudits] = useState<IDetailedAudit[]>([]);
  const [selectedAuditId, setSelectedAuditId] = useState<IAudit['id']>();
  const [isAuditCriteriaModalOpen, setIsAuditCriteriaModalOpen] =
    useState(false);
  const [isEditAuditModalOpen, setIsEditAuditModalOpen] = useState(false);
  const [isCreateAuditModalOpen, setIsCreateAuditModalOpen] = useState(false);
  const [auditsPage, setAuditsPage] = useState(1);
  const [auditsPerPage, setAuditsPerPage] = useState(10);
  const [auditsPaginationTotal, setAuditsPaginationTotal] = useState(0);

  const location = useLocation();
  const isAssessmentsRoute = location.pathname.startsWith('/app/assessments');

  const filtersOrganized = useMemo(
    () => ({ area_ids: filters.area_ids?.value }),
    [filters.area_ids?.value]
  );
  const assessmentsQuery = useAssessmentsQuery(filtersOrganized, { enabled: isAssessmentsRoute });
  const auditsQuery = useAuditsQuery(
    filtersOrganized,
    auditsPage,
    auditsPerPage,
    { enabled: isAssessmentsRoute }
  );
  const assessments = assessmentsQuery.data ?? [];
  const audits = auditsQuery.data?.data ?? [];
  const loading = assessmentsQuery.isLoading || auditsQuery.isLoading;

  const getDateLabel = (
    filterName: string,
    from: Date | null,
    to: Date | null
  ) => {
    if (!from && !to) return '';
    let label = filterName + ' -';
    if (from) label += ' od: ' + formatDate(from);
    if (to) label += ' do: ' + formatDate(to);
    return label;
  };

  const handleLoadAssessments = useCallback(() => {
    assessmentsQuery.refetch();
  }, [assessmentsQuery]);

  const handleLoadAudits = useCallback(() => {
    auditsQuery.refetch();
  }, [auditsQuery]);

  useEffect(() => {
    if (auditsQuery.data?.pagination?.count != null) {
      setAuditsPaginationTotal(Number(auditsQuery.data.pagination.count));
    }
  }, [auditsQuery.data?.pagination?.count]);

  const setAssessmentsNoop = useCallback((_: React.SetStateAction<IAssessment[]>) => {}, []);
  const setAuditsNoop = useCallback((_: React.SetStateAction<IAudit[]>) => {}, []);
  const setLoadingNoop = useCallback((_: React.SetStateAction<boolean>) => {}, []);

  return (
    <AssessmentContext.Provider
      value={{
        tabIndex,
        setTabIndex,
        areas,
        setAreas,
        assessments,
        setAssessments: setAssessmentsNoop,
        selectedBlock,
        setSelectedBlock,
        selectedAssessments,
        setSelectedAssessments,
        selectedAssessment,
        setSelectedAssessment,
        assessmentToEdit,
        setAssessmentToEdit,
        assessmentToEditId,
        setAssessmentToEditId,
        assessmentsToLaunch,
        setAssessmentsToLaunch,
        filters,
        defaultFilters,
        savedFilters,
        setFilters,
        setDefaultFilters,
        setSavedFilters,
        getDateLabel,
        audits,
        setAudits: setAuditsNoop,
        selectedAudit,
        setSelectedAudit,
        selectedAudits,
        setSelectedAudits,
        selectedAuditId,
        setSelectedAuditId,
        isAuditCriteriaModalOpen,
        setIsAuditCriteriaModalOpen,
        isEditAuditModalOpen,
        setIsEditAuditModalOpen,
        isCreateAuditModalOpen,
        setIsCreateAuditModalOpen,
        handleLoadAssessments,
        handleLoadAudits,
        loading,
        setLoading: setLoadingNoop,
        auditsPage,
        setAuditsPage,
        auditsPerPage,
        setAuditsPerPage,
        auditsPaginationTotal,
        setAuditsPaginationTotal
      }}
    >
      {children}
    </AssessmentContext.Provider>
  );
};

export const useAssessmentContext = () => {
  const context = useContext(AssessmentContext);
  if (!context) {
    throw new Error(
      'useAssessmentContext must be inside a AssessmentContextProvider with a value'
    );
  }
  return context;
};
